/**
 * Vakantiesaldo in lagen: wettelijk en bovenwettelijk per opbouwjaar, met
 * vervaldatum, FIFO-afboeking en verval dat de kennisgeving volgt.
 *
 * Pure functies, geen Supabase. De loader (vakantie-saldo-laden.ts) zet
 * contracten, potjes, entries en kennisgevingen om in de invoer hieronder.
 *
 * Regels:
 * - Lagen worden berekend, niet opgeslagen. Basis per jaar komt uit het
 *   contractregime (contract-regime.ts: vakantieBasisPerLaag); de 10%-opbouw
 *   per loonperiode landt in de wettelijke laag van het jaar waarin de
 *   loonperiode eindigt. Overdrachten en bijzondere potjes zijn rijen in
 *   leave_entitlements (laag, jaar, valid_until = vervaldatum).
 * - Vervaldata: wettelijk jaar J vervalt 1 juli J+1 (art. 7:640a BW);
 *   bovenwettelijk jaar J vervalt 31 december J+5 (art. 7:642 BW).
 * - FIFO op vervaldatum: elke vakantie-entry wordt chronologisch toegerekend
 *   aan de laag die op de entry-datum nog geldig is, in het jaar van de entry
 *   of eerder (geen voorschot op een toekomstig jaar), en het eerst vervalt.
 *   Past geen laag, dan telt de entry als `voorschot` (negatief, zichtbaar).
 * - Verval volgt kennisgeving: een wettelijke laag telt pas als vervallen
 *   wanneer er een kennisgeving (soort feb of jun) mét verstuurd_op voor
 *   bestaat (HvJ C-684/16 Max-Planck, HR 23-06-2023). Ontbreekt die, dan
 *   krijgt de laag status `vervallen_geblokkeerd`, blijft hij beschikbaar en
 *   kan hij ook ná de vervaldatum nog worden opgenomen. Bovenwettelijk
 *   vervalt zonder die eis.
 * - `vervalt_binnenkort` = vervaldatum binnen 90 dagen na de peildatum.
 * - Alle uren afgerond op 0,01.
 *
 * ═══ BRON VAN WAARHEID ═══
 * Byte-identieke spiegels (Vercel-functions importeren niet buiten hun
 * app-root, Vite-apps geen workspace-deps). Wijzig je iets, wijzig dan ALLE
 * DRIE en draai scripts/smoke-workforce.sh (hash-pariteitscheck):
 *   1. brands/cinco/apps/office-app/api/_lib/vakantie-saldo.ts   (canoniek)
 *   2. brands/cinco/apps/office-app/src/lib/vakantie-saldo.ts
 *   3. apps/team-app/api/_lib/vakantie-saldo.ts
 */

export type VakantieLaag = 'wettelijk' | 'bovenwettelijk';
export type LaagBron = 'basis' | 'opbouw' | 'overdracht' | 'potje';
export type KennisgevingSoort = 'feb' | 'jun' | 'okt' | 'vervallen';
export type LaagStatus = 'open' | 'vervalt_binnenkort' | 'vervallen' | 'vervallen_geblokkeerd';

export interface LaagInput {
  jaar: number;
  laag: VakantieLaag;
  bron: LaagBron;
  uren: number;
  vervalt_op: string; // YYYY-MM-DD
  omschrijving?: string;
  entitlement_id?: string;
}

export interface Kennisgeving {
  jaar: number;
  laag: VakantieLaag;
  soort: KennisgevingSoort;
  verstuurd_op: string | null;
}

export interface VakantieEntry {
  date: string; // YYYY-MM-DD
  hours: number;
}

export interface LaagStand extends LaagInput {
  opgenomen: number;
  /** nog beschikbaar (0 zodra vervallen) */
  resterend: number;
  /** uren die daadwerkelijk vervallen zijn */
  vervallen: number;
  status: LaagStatus;
  /** laatste verstuurde kennisgeving over deze laag (feb/jun/okt) */
  kennisgeving_op: string | null;
}

export interface VakantieSaldo {
  peildatum: string;
  lagen: LaagStand[];
  /** Σ resterend van niet-vervallen lagen − voorschot */
  beschikbaar: number;
  /** opgenomen uren waar geen laag voor was */
  voorschot: number;
  /** totaal opgenomen (alle entries, incl. voorschot) */
  opgenomen: number;
  /** totaal vervallen */
  vervallen: number;
  /** eerstvolgende vervaldatum met nog resterende uren */
  vervalt_eerst: { vervalt_op: string; uren: number; laag: VakantieLaag; jaar: number } | null;
}

export const VERVALT_BINNENKORT_DAGEN = 90;
const GEEN_VERVAL = '9999-12-31';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function jaarVan(dateISO: string): number {
  return parseInt(dateISO.slice(0, 4), 10);
}

function dagenTussen(a: string, b: string): number {
  return Math.round((new Date(b + 'T00:00:00').getTime() - new Date(a + 'T00:00:00').getTime()) / 86400000);
}

/** Wettelijke dagen van jaar J vervallen op 1 juli J+1. */
export function vervaldatumWettelijk(jaar: number): string {
  return `${jaar + 1}-07-01`;
}

/** Bovenwettelijke dagen van jaar J vervallen op 31 december J+5. */
export function vervaldatumBovenwettelijk(jaar: number): string {
  return `${jaar + 5}-12-31`;
}

export function vervaldatumVoor(laag: VakantieLaag, jaar: number): string {
  return laag === 'wettelijk' ? vervaldatumWettelijk(jaar) : vervaldatumBovenwettelijk(jaar);
}

/**
 * Lagen van één opbouwjaar uit de contractbasis en de 10%-opbouw.
 * De opbouw is precies de wettelijke opbouw over die uren en landt in de
 * wettelijke laag. Lege lagen (0 uur) worden niet aangemaakt.
 */
export function lagenVoorJaar(
  jaar: number,
  basis: { wettelijk: number; bovenwettelijk: number },
  opbouwWettelijk: number,
): LaagInput[] {
  const uit: LaagInput[] = [];
  const wettelijk = round2(basis.wettelijk + opbouwWettelijk);
  if (wettelijk > 0) {
    const bron: LaagBron = basis.wettelijk > 0 ? 'basis' : 'opbouw';
    const delen = [
      basis.wettelijk > 0 ? `basis ${round2(basis.wettelijk)}u` : null,
      opbouwWettelijk > 0 ? `opbouw ${round2(opbouwWettelijk)}u` : null,
    ].filter(Boolean);
    uit.push({ jaar, laag: 'wettelijk', bron, uren: wettelijk, vervalt_op: vervaldatumWettelijk(jaar), omschrijving: `Wettelijk ${jaar} (${delen.join(' + ')})` });
  }
  const boven = round2(basis.bovenwettelijk);
  if (boven > 0) {
    uit.push({ jaar, laag: 'bovenwettelijk', bron: 'basis', uren: boven, vervalt_op: vervaldatumBovenwettelijk(jaar), omschrijving: `Bovenwettelijk ${jaar}` });
  }
  return uit;
}

function kennisgevingVerstuurd(k: Kennisgeving[], laag: LaagInput, soorten: KennisgevingSoort[]): string | null {
  const hits = k
    .filter(x => x.jaar === laag.jaar && x.laag === laag.laag && soorten.includes(x.soort) && !!x.verstuurd_op)
    .map(x => x.verstuurd_op as string)
    .sort();
  return hits.length ? hits[hits.length - 1]! : null;
}

/** Tot wanneer de laag feitelijk opneembaar is: wettelijk zonder kennisgeving vervalt niet. */
function geldigTot(laag: LaagInput, kennisgevingen: Kennisgeving[]): string {
  if (laag.laag === 'bovenwettelijk') return laag.vervalt_op;
  return kennisgevingVerstuurd(kennisgevingen, laag, ['feb', 'jun']) ? laag.vervalt_op : GEEN_VERVAL;
}

function vergelijkLagen(a: LaagStand, b: LaagStand): number {
  if (a.vervalt_op !== b.vervalt_op) return a.vervalt_op.localeCompare(b.vervalt_op);
  if (a.jaar !== b.jaar) return a.jaar - b.jaar;
  if (a.laag !== b.laag) return a.laag === 'wettelijk' ? -1 : 1;
  return 0;
}

/**
 * Het saldo op een peildatum.
 */
export function berekenVakantieSaldo(
  peildatum: string,
  lagen: LaagInput[],
  entries: VakantieEntry[],
  kennisgevingen: Kennisgeving[],
): VakantieSaldo {
  const stands: LaagStand[] = lagen.map(l => ({
    ...l,
    uren: round2(l.uren),
    opgenomen: 0,
    resterend: round2(l.uren),
    vervallen: 0,
    status: 'open',
    kennisgeving_op: kennisgevingVerstuurd(kennisgevingen, l, ['feb', 'jun', 'okt']),
  }));
  const opneembaarTot = new Map<LaagStand, string>(stands.map(s => [s, geldigTot(s, kennisgevingen)]));

  // ── FIFO op vervaldatum ──
  let voorschot = 0;
  let opgenomenTotaal = 0;
  const gesorteerd = [...entries].filter(e => e.hours > 0).sort((a, b) => a.date.localeCompare(b.date));
  for (const e of gesorteerd) {
    let rest = e.hours;
    opgenomenTotaal += e.hours;
    const jaarEntry = jaarVan(e.date);
    const kandidaten = stands
      .filter(s => s.resterend > 0.0001 && s.jaar <= jaarEntry && (opneembaarTot.get(s) ?? s.vervalt_op) >= e.date)
      .sort(vergelijkLagen);
    for (const s of kandidaten) {
      if (rest <= 0.0001) break;
      const neem = Math.min(rest, s.resterend);
      s.opgenomen = round2(s.opgenomen + neem);
      s.resterend = round2(s.resterend - neem);
      rest = round2(rest - neem);
    }
    if (rest > 0.0001) voorschot = round2(voorschot + rest);
  }

  // ── Verval en status ──
  let vervallenTotaal = 0;
  for (const s of stands) {
    const verstreken = peildatum > s.vervalt_op;
    if (verstreken) {
      const magVervallen = s.laag === 'bovenwettelijk' || !!kennisgevingVerstuurd(kennisgevingen, s, ['feb', 'jun']);
      if (magVervallen) {
        s.vervallen = s.resterend;
        s.resterend = 0;
        s.status = 'vervallen';
        vervallenTotaal += s.vervallen;
      } else {
        s.status = 'vervallen_geblokkeerd';
      }
    } else if (dagenTussen(peildatum, s.vervalt_op) <= VERVALT_BINNENKORT_DAGEN) {
      s.status = 'vervalt_binnenkort';
    }
  }

  const beschikbaar = round2(stands.reduce((sum, s) => sum + s.resterend, 0) - voorschot);
  const metRest = stands
    .filter(s => s.resterend > 0.0001 && s.status !== 'vervallen')
    .sort(vergelijkLagen);
  const eerst = metRest[0];

  return {
    peildatum,
    lagen: stands.sort(vergelijkLagen),
    beschikbaar,
    voorschot: round2(voorschot),
    opgenomen: round2(opgenomenTotaal),
    vervallen: round2(vervallenTotaal),
    vervalt_eerst: eerst ? { vervalt_op: eerst.vervalt_op, uren: eerst.resterend, laag: eerst.laag, jaar: eerst.jaar } : null,
  };
}

/** Nederlandse datumweergave voor mail en kaart: 1 juli 2027. */
export function datumLang(dateISO: string): string {
  const m = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  const [y, mo, d] = dateISO.split('-').map(Number);
  return `${d} ${m[(mo ?? 1) - 1]} ${y}`;
}

/** Uren als "12,5u". */
export function urenTekst(n: number): string {
  return `${round2(n).toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}u`;
}
